import { ClassAttributes, ComponentProps, HTMLAttributes } from "react";
import { Text } from "../../core/Text";
import { Interpolation, Theme, css, useTheme } from "@emotion/react";
import { Button } from "components/core/buttons/Button";
import { useAuth } from "hooks/useAuth";
import { getDday } from "utils/dday";

const ItemValue = (
  props: Omit<ComponentProps<typeof Text>, "variant" | "color">,
) => <Text variant="body3" color="text.primary" {...props} />;

export function ContentCTAButton({
  ctaText,
  disabledText,
  ...props
}: {
  ctaText: string;
  disabledText?: string;
} & ComponentProps<typeof Button>) {
  return (
    <Button
      css={css`
        width: 100%;
        letter-spacing: 0em;
      `}
      size="large"
      {...props}
    >
      {props.disabled ? disabledText || ctaText : ctaText}
    </Button>
  );
}

export function ProductCTAButton({
  product,
  onPaymentBtnClick,
  onGuestPaymentBtnClick,
  ...props
}: {
  product: ProductFragment;
  onPaymentBtnClick: () => void;
  onGuestPaymentBtnClick?: () => void;
} & ComponentProps<typeof ContentCTAButton>) {
  const theme = useTheme();
  const { me } = useAuth();

  return (
    <>
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 0;
          border-top: 1px solid ${theme.palette.text.focusVisible};
        `}
      >
        <ItemValue>가격</ItemValue>
        <Text
          css={css`
            font-weight: 400;
            color: #cd0000;
          `}
          variant="subtitle1"
        >
          {`${(product.on_discount
            ? product.discount_price
            : product.price
          ).toLocaleString()}원`}
        </Text>
      </div>
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 8px;
        `}
      >
        {product.on_discount && (
          <Text
            css={css`
              display: flex;
              justify-content: center;
              font-weight: 500;
            `}
            variant="caption1"
            color="primary.main"
          >
            {`${
              getDday(product.discount_end_time) > 0
                ? `${getDday(product.discount_end_time)}일 이후에`
                : "오늘"
            } 할인이 종료됩니다`}
          </Text>
        )}
        <ContentCTAButton onClick={onPaymentBtnClick} {...props} />
        {!me &&
          product.on_order &&
          product.is_guest_payment_allowed &&
          onGuestPaymentBtnClick && (
            <>
              <ContentCTAButton
                {...props}
                ctaText={"비회원으로 " + props.ctaText}
                variant="filled"
                color="secondary"
                onClick={onGuestPaymentBtnClick}
              />
              <GuestPaymentNotice />
            </>
          )}
      </div>
    </>
  );
}

export const GuestPaymentNotice = (
  props: ClassAttributes<HTMLDivElement> &
    HTMLAttributes<HTMLDivElement> & {
      css?: Interpolation<Theme>;
    },
) => {
  const theme = useTheme();

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 6px;
        padding: 10px 12px;
        border-radius: ${theme.shape.radius.medium}px;
        background: ${theme.palette.grey[100]};
      `}
      {...props}
    >
      <div
        css={css`
          font-size: 11px;
          font-weight: 500;
          line-height: 140%; /* 15.4px */
          color: ${theme.palette.text.primary};
        `}
      >
        비회원 결제 시 유의사항
      </div>
      <p
        css={css`
          font-size: 10px;
          font-weight: 400;
          line-height: 140%; /* 14px */
          color: ${theme.palette.text.secondary};
        `}
      >
        {`비회원으로 결제하시면 믹스 홈페이지에서 구매내역을 조회하실 수 없습니다. 유료 결제의 경우, 입력하신 이메일로 구매내역이 발송되므로 이메일 주소를 정확히 입력해 주시기 바랍니다. 결제 취소는 믹스 홈페이지 우측 하단의 톡 버튼 또는 mix@team-mint.io로 문의해 주세요.`}
      </p>
    </div>
  );
};
